import React, { useEffect, ReactNode, createContext, useState } from 'react'
import telemetryService, { TelemetryConfig } from './telemetryService'
import { withAITracking } from '@microsoft/applicationinsights-react-js'
import { TelemetryContextType } from './telemetry.types'

interface TelemetryProviderProps {
  config: TelemetryConfig
  children: ReactNode
}

export const TelemetryContext = createContext<TelemetryContextType | undefined>(
  undefined,
)

export const TelemetryProvider: React.FC<TelemetryProviderProps> = ({
  config,
  children,
}) => {
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    // Initialize telemetry asynchronously after a delay to not block initial render
    const initTimeout = setTimeout(() => {
      try {
        telemetryService.initialize(config);
        setIsInitialized(true);
      } catch (error) {
        console.error('Telemetry initialization failed:', error);
        // Still mark as initialized to prevent blocking
        setIsInitialized(true);
      }
    }, 500);

    return () => clearTimeout(initTimeout);
  }, [config]);

  // Provide safe fallback methods while telemetry is initializing
  const contextValue: TelemetryContextType = {
    trackEvent: (name, properties) => {
      if (isInitialized) {
        telemetryService.trackEvent(name, properties);
      }
    },
    trackException: (error, properties) => {
      if (isInitialized) {
        telemetryService.trackException(error, properties);
      }
    },
    trackTrace: (message, properties) => {
      if (isInitialized) {
        telemetryService.trackTrace(message, properties);
      }
    },
    trackPageView: (name, properties) => {
      if (isInitialized) {
        telemetryService.trackPageView(name, properties);
      }
    },
    addContext: (key, value) => {
      if (isInitialized) {
        telemetryService.addContext(key, value);
      }
    },
  }

  return (
    <TelemetryContext.Provider value={contextValue}>
      {children}
    </TelemetryContext.Provider>
  )
}

export function withTelemetry<P extends object>(
  Component: React.ComponentType<P>,
): React.ComponentType<P> {
  const reactPlugin = telemetryService.getReactPlugin()
  if (!reactPlugin) {
    console.warn(
      'React plugin not initialized, withTelemetry will not track component',
    )
    return Component
  }
  return withAITracking(reactPlugin, Component)
}
